import React, { useState, useEffect } from 'react';
import { User, Calendar, Star, MessageCircle, TrendingUp, Award, Eye, X, MapPin, Clock, Flame } from 'lucide-react';
import API_URL from '../config/ApiUrl';
import { getSmokerEmoji } from '../static/SmokerEmoji';
import { AchievementsCardPublicView } from './AchievementsPublicView';
import { UserLevelCardPublicView } from './UserLevelCardPublicView';
import { useTheme } from '../arrangement/ThemeContext';
const PublicProfileView = ({ userId, onClose, currentUserId }) => {
  const [profile, setProfile] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);
  const { colors } = useTheme();
  
  const [userStats, setUserStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  useEffect(() => {
    if (userId) {
      fetchPublicProfile();
    }
  }, [userId]);

  const fetchPublicProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch user public profile - FIX: Correct API endpoint and response handling
      const profileResponse = await fetch(`${API_URL}/public-profile/${userId}`, {
        credentials: 'include'
      });
      
      if (!profileResponse.ok) {
        throw new Error(`HTTP error! status: ${profileResponse.status}`);
      }
      
      const profileResult = await profileResponse.json();
      console.log('Profile API Response:', profileResult);
      
      if (!profileResult.success) {
        throw new Error(profileResult.error || 'Failed to fetch profile');
      }
      
      // FIX: Extract data from the response structure
      setProfile(profileResult.data);
      
      // Fetch user stats - FIX: Correct response handling
      const statsResponse = await fetch(`${API_URL}/api/user/stats/${userId}`, {
        credentials: 'include'
      });
      
      if (statsResponse.ok) {
        const statsResult = await statsResponse.json();
        console.log('Stats API Response:', statsResult);
        
        if (statsResult.success) {
          setUserStats(statsResult.data);
        }
      }
      
      // Fetch recent activity - FIX: Correct response handling  
      const activityResponse = await fetch(`${API_URL}/api/user/recent-activity/${userId}?per_page=10`, {
        credentials: 'include'
      });
      
      if (activityResponse.ok) {
        const activityResult = await activityResponse.json();
        console.log('Activity API Response:', activityResult);
        
        if (activityResult.success) {
          setRecentActivity(activityResult.data || []); // Handle both array and object responses
        }
      }
      
    } catch (error) {
      console.error('Failed to fetch public profile:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getSmokerLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'newbie': return '#10b981';
      case 'casual': return '#3b82f6';
      case 'regular': return '#8b5cf6';
      case 'connoisseur': return '#f59e0b';
      case 'expert': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getActivityTypeInfo = (type) => {
    switch (type) {
      case 'thread':
        return {
          icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>,
          color: '#8b5cf6',
          bgColor: '#8b5cf620',
          borderColor: '#8b5cf640',
          label: 'Thread'
        };
      case 'comment':
        return {
          icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>,
          color: '#00d4aa',
          bgColor: '#00d4aa20',
          borderColor: '#00d4aa40',
          label: 'Comment'
        };
      case 'research_article':
        return {
          icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
          </svg>,
          color: '#3b82f6',
          bgColor: '#3b82f620',
          borderColor: '#3b82f640',
          label: 'Research Article'
        };
      case 'article_discussion':
        return {
          icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="12" r="1"></circle>
            <circle cx="15" cy="12" r="1"></circle>
            <circle cx="21" cy="12" r="1"></circle>
          </svg>,
          color: '#f59e0b',
          bgColor: '#f59e0b20',
          borderColor: '#f59e0b40',
          label: 'Article Discussion'
        };
      case 'poll':
        return {
          icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="20" x2="12" y2="10"></line>
            <line x1="18" y1="20" x2="18" y2="4"></line>
            <line x1="6" y1="20" x2="6" y2="16"></line>
          </svg>,
          color: '#ef4444',
          bgColor: '#ef444420',
          borderColor: '#ef444440',
          label: 'Poll'
        };
      default:
        return {
          icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"></polyline>
          </svg>,
          color: '#666',
          bgColor: '#66620',
          borderColor: '#66640',
          label: 'Activity'
        };
    }
  };

  const fetchUserStats = async (userId) => {
    try {
      setStatsLoading(true);
      const response = await fetch(`${API_URL}/api/user/stats/${userId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      const result = await response.json();
      console.log(result)
      if (result.success) {
        setUserStats(result.data);
      } else {
        console.error('Failed to fetch user stats:', result.error);
        setUserStats(null);
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      setUserStats(null);
    } finally {
      setStatsLoading(false);
    }
  };
  useEffect(() => {
  if (profile?.user_id) {
    fetchUserStats(profile.user_id);
  }
}, [profile]);
  const getSmokerLevelIcon = (level) => {
    switch (level?.toLowerCase()) {
      case 'newbie': return '🌱';
      case 'casual': return '🍃';
      case 'regular': return '🌿';
      case 'connoisseur': return '🔥';
      case 'expert': return '👑';
      default: return '🚬';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'thread': return <MessageCircle size={16} style={{ color: '#8b5cf6' }} />;
      case 'comment': return <MessageCircle size={16} style={{ color: '#3b82f6' }} />;
      case 'poll': return <div style={{ color: '#f59e0b', fontSize: '16px' }}>🗳️</div>;
      case 'research_article': return <div style={{ color: '#10b981', fontSize: '16px' }}>📚</div>;
      case 'article_discussion': return <div style={{ color: '#ef4444', fontSize: '16px' }}>💬</div>;
      default: return <Eye size={16} style={{ color: '#6b7280' }} />;
    }
  };

  const formatTimeAgo = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>👤</div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div style={{ fontSize: '32px', marginBottom: '16px' }}>❌</div>
          <p>{error || 'Profile not found'}</p>
          <button
            onClick={onClose}
            style={{
              marginTop: '16px',
              padding: '8px 16px',
              background: '#333',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.95)',
      zIndex: 1000,
      overflow: 'auto'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '24px',
        minHeight: '100vh',
        background: '#0a0a0a'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '32px'
        }}>
          <h1 style={{
            margin: 0,
            color: 'white',
            fontSize: '24px',
            fontWeight: '700'
          }}>
            User Profile
          </h1>
          
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              padding: '8px'
            }}
          >
            <X size={24} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{
              background: colors.bgTertiary,
              borderRadius: '20px',
              padding: '32px',
              border: '1px solid #333',
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
                {/* Profile Picture */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  <img
                    src={
                      profile.avatar || 
                      profile.user_avatar || 
                      (profile.cover_image ? `https://highmind-bucket.s3.us-east-1.amazonaws.com/${profile.cover_image}` : null) ||
                      `https://ui-avatars.com/api/?name=${profile.username}&background=8b5cf6&color=fff&size=100`
                    }
                    alt={profile.display_name || profile.username}
                    style={{
                      width: '100px',
                      height: '100px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '3px solid #333'
                    }}
                  />
                  
                  {profile.smoker_level && (
                    <img 
                      src={getSmokerEmoji(profile.smoker_level)}
                      alt={`${profile.smoker_level} level`}
                      style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  )}
                </div>

                {/* Profile Info */}
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16%', marginBottom: '12px' }}>
                    <h2 style={{
                      margin: 0,
                      color: '#e5e5e5',
                      fontSize: '28px',
                      fontWeight: '700'
                    }}>
                      {profile.display_name || profile.username}
                    </h2>
                    
                    {profile.username !== profile.display_name && (
                      <span style={{
                        color: '#888',
                        fontSize: '18px'
                      }}>
                        @{profile.username}
                      </span>
                    )}
                  </div>

                  {/* Level Info */}
                  {profile.level && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 12px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      color: 'white',
                      borderRadius: '12px',
                      marginBottom: '12px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      Level {profile.level} - {profile.level_title}
                    </div>
                  )}

                  {/* Smoker Level Badge */}
                  {profile.smoker_level && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      background: `${getSmokerLevelColor(profile.smoker_level)}20`,
                      border: `2px solid ${getSmokerLevelColor(profile.smoker_level)}40`,
                      borderRadius: '16px',
                      marginBottom: '16px',
                      marginLeft: '8px'
                    }}>
                      <span style={{ fontSize: '16px' }}>
                        {getSmokerLevelIcon(profile.smoker_level)}
                      </span>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: getSmokerLevelColor(profile.smoker_level),
                        textTransform: 'capitalize'
                      }}>
                        {profile.smoker_level}
                      </span>
                    </div>
                  )}

                  {/* Bio */}
                  {profile.bio && (
                    <p style={{
                      color: '#b3b3b3',
                      fontSize: '16px',
                      lineHeight: '1.6',
                      marginBottom: '16px'
                    }}>
                      {profile.bio}
                    </p>
                  )}

                  {/* Location */}
                  {profile.location && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: '#888',
                      marginBottom: '16px'
                    }}>
                      <MapPin size={16} />
                      <span>{profile.location}</span>
                    </div>
                  )}

                  {/* Join Date */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: '#888'
                  }}>
                    <Calendar size={16} />
                    <span>Joined {new Date(profile.joined_date || profile.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
                </div>

            </div>
          <div style={{ width: '300px', flexShrink: 0 }}>
            <UserLevelCardPublicView 
              stats={userStats} 
              isLoading={statsLoading} 
              username={profile?.username || profile?.display_name}
            />
          </div>
        </div>
        {/* Stats Cards */}
        {userStats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: colors.bgTertiary,
              border: '1px solid #333',
              borderRadius: '12px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#00d4aa',
                marginBottom: '4px'
              }}>
                {userStats.total_reviews || 0}
              </div>
              <div style={{ color: '#888', fontSize: '14px' }}>Reviews Written</div>
            </div>

            <div style={{
              background: colors.bgTertiary,
              border: '1px solid #333',
              borderRadius: '12px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#8b5cf6',
                marginBottom: '4px'
              }}>
                {userStats.total_threads || 0}
              </div>
              <div style={{ color: '#888', fontSize: '14px' }}>Threads Created</div>
            </div>

            <div style={{
              background: colors.bgTertiary,
              border: '1px solid #333',
              borderRadius: '12px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#3b82f6',
                marginBottom: '4px'
              }}>
                {userStats.total_comments || 0}
              </div>
              <div style={{ color: '#888', fontSize: '14px' }}>Comments Posted</div>
            </div>

            <div style={{
              background: colors.bgTertiary,
              border: '1px solid #333',
              borderRadius: '12px',
              padding: '20px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '24px',
                fontWeight: '700',
                color: '#f59e0b',
                marginBottom: '4px'
              }}>
                {userStats.total_upvotes || 0}
              </div>
              <div style={{ color: '#888', fontSize: '14px' }}>Total Likes</div>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '24px',
          background: colors.bgTertiary,
          padding: '4px',
          borderRadius: '12px',
          border: '1px solid #333'
        }}>
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'activity', label: 'Recent Activity' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: activeTab === tab.key 
                  ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
                  : 'transparent',
                color: activeTab === tab.key ? '#0a0a0a' : '#888',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{
          background: colors.bgTertiary,
          border: '1px solid #333',
          borderRadius: '16px',
          padding: '24px'
        }}>
          {activeTab === 'overview' && (
            <div>
              <h3 style={{
                color: '#e5e5e5',
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '16px'
              }}>
                Profile Overview
              </h3>
              
              {/* Achievements */}
              {profile.achievements && profile.achievements.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  {(() => {
                    console.log('All achievements:', profile.achievements);
                    
                    // First, get achievements with display_order
                    const withDisplayOrder = profile.achievements
                      .filter(achievement => achievement.display_order !== null && achievement.display_order !== undefined)
                      .sort((a, b) => a.display_order - b.display_order);
                    
                    console.log('With display_order:', withDisplayOrder);
                    
                    let displayed = [];
                    
                    if (withDisplayOrder.length > 0) {
                      // User has set display preferences
                      displayed = withDisplayOrder;
                    } else {
                      // No display preferences set - show up to 6 achievements
                      // Prioritize completed achievements first
                      const completed = profile.achievements.filter(a => a.progress === 100);
                      const incomplete = profile.achievements.filter(a => a.progress < 100);
                      
                      // Take up to 6, prioritizing completed ones
                      displayed = [...completed, ...incomplete].slice(0, 6);
                    }
                    
                    console.log('Final displayed achievements:', displayed);
                    
                    return (
                      <AchievementsCardPublicView 
                        profileData={{
                          displayed_achievements: displayed,
                          achievements: profile.achievements
                        }}
                        loading={false}
                        username={profile.username || profile.display_name}
                      />
                    );
                  })()}
                </div>
              )}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '16px', 
                marginBottom: '24px' 
              }}>
                {profile.tolerance_level && (
                  <div>
                    <h4 style={{ color: '#e5e5e5', marginBottom: '8px', fontSize: '14px' }}>Tolerance Level</h4>
                    <span
                      style={{
                        padding: '8px 16px',
                        background: profile.tolerance_level.toLowerCase() === 'high' 
                          ? 'rgba(239, 68, 68, 0.2)' 
                          : profile.tolerance_level.toLowerCase() === 'medium' 
                          ? 'rgba(245, 158, 11, 0.2)' 
                          : 'rgba(34, 197, 94, 0.2)',
                        color: profile.tolerance_level.toLowerCase() === 'high' 
                          ? '#ef4444' 
                          : profile.tolerance_level.toLowerCase() === 'medium' 
                          ? '#f59e0b' 
                          : '#22c55e',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: '600',
                        textTransform: 'capitalize',
                        display: 'inline-block',
                        width: '90%',
                        textAlign: 'center'
                      }}
                    >
                      {profile.tolerance_level} Tolerance
                    </span>
                  </div>
                )}

                {profile.years_of_experience && (
                  <div>
                    <h4 style={{ color: '#e5e5e5', marginBottom: '8px', fontSize: '14px' }}>Experience</h4>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      background: 'rgba(139, 92, 246, 0.2)',
                      borderRadius: '12px',
                      width: '90%'
                    }}>
                      <span style={{ fontSize: '16px' }}>🏆</span>
                      <span style={{
                        color: '#8b5cf6',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        {profile.years_of_experience} {profile.years_of_experience === 1 ? 'Year' : 'Years'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Consumption Preferences */}
              {profile.consumption_methods && profile.consumption_methods.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#e5e5e5', marginBottom: '12px', fontSize: '16px' }}>Consumption Methods</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {profile.consumption_methods.filter(method => method !== "Select your methods").map((method) => (
                      <span
                        key={method}
                        style={{
                          padding: '6px 14px',
                          background: 'rgba(245, 158, 11, 0.2)',
                          color: '#f59e0b',
                          borderRadius: '10px',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}
                      >
                        {method}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Favorite Strains */}
              {profile.favorite_strains && profile.favorite_strains.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ color: '#e5e5e5', marginBottom: '12px', fontSize: '16px' }}>Favorite Strains</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {profile.favorite_strains.map((strain, index) => (
                      <span
                        key={strain}
                        style={{
                          padding: '6px 14px',
                          background: index % 2 === 0 ? 'rgba(0, 212, 170, 0.2)' : 'rgba(139, 92, 246, 0.2)',
                          color: index % 2 === 0 ? '#00d4aa' : '#8b5cf6',
                          borderRadius: '10px',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}
                      >
                        {strain}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div>
              <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '18px',
                fontWeight: '600',
                color: '#e5e5e5',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{ color: '#00d4aa' }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"></polyline>
                  </svg>
                </div>
                Recent Activity
              </h3>
              
              {recentActivity && recentActivity.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {recentActivity.map((activity, index) => {
                    const activityTypeInfo = getActivityTypeInfo(activity.type);
                    
                    return (
                      <div
                        key={activity.id || index}
                        style={{
                          background: 'linear-gradient(135deg, #1f1f1f 0%, #2a2a2a 100%)',
                          borderRadius: '16px',
                          border: '1px solid #333',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.borderColor = activityTypeInfo.borderColor;
                          e.currentTarget.style.boxShadow = `0 8px 32px ${activityTypeInfo.color}20`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.borderColor = '#333';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        {/* Activity Header */}
                        <div style={{
                          padding: '16px 20px 12px 20px',
                          borderBottom: '1px solid #333'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '8px'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              {/* Activity Type Badge */}
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 10px',
                                background: activityTypeInfo.bgColor,
                                border: `1px solid ${activityTypeInfo.borderColor}`,
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                color: activityTypeInfo.color
                              }}>
                                {activityTypeInfo.icon}
                                <span>{activityTypeInfo.label}</span>
                              </div>

                              {/* Sesh Room Badge (if applicable) */}
                              {activity.sesh_room_name && (
                                <div style={{
                                  padding: '4px 8px',
                                  background: 'rgba(59, 130, 246, 0.2)',
                                  border: '1px solid rgba(59, 130, 246, 0.4)',
                                  borderRadius: '8px',
                                  fontSize: '11px',
                                  fontWeight: '500',
                                  color: '#3b82f6'
                                }}>
                                  {activity.sesh_room_name}
                                </div>
                              )}
                            </div>

                            {/* Timestamp */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              color: '#888',
                              fontSize: '12px'
                            }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12,6 12,12 16,14"></polyline>
                              </svg>
                              <span>{formatTimeAgo(activity.created_at)}</span>
                            </div>
                          </div>

                          {/* Activity Title */}
                          <h4 style={{
                            margin: 0,
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#e5e5e5',
                            lineHeight: '1.4',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {activity.title}
                          </h4>
                        </div>

                        {/* Activity Content */}
                        <div style={{ padding: '0 20px 16px 20px' }}>
                          {activity.content && (
                            <p style={{
                              margin: '0 0 12px 0',
                              color: '#b3b3b3',
                              fontSize: '14px',
                              lineHeight: '1.5',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {activity.content}
                            </p>
                          )}

                          {/* Activity Stats */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            fontSize: '13px',
                            color: '#888'
                          }}>
                            {/* Upvotes */}
                          
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 8px',
                                background: (activity.likes || 0) >= 0 
                                  ? 'rgba(0, 212, 170, 0.1)' 
                                  : 'rgba(239, 68, 68, 0.1)',
                                borderRadius: '8px',
                                border: (activity.likes || 0) >= 0 
                                  ? '1px solid rgba(0, 212, 170, 0.2)' 
                                  : '1px solid rgba(239, 68, 68, 0.2)'
                              }}>
                                {(activity.likes || 0) >= 0 ? (
                                  // Upvote arrow
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#00d4aa' }}>
                                    <path d="m18 15-6-6-6 6"/>
                                  </svg>
                                ) : (
                                  // Downvote arrow
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#ef4444' }}>
                                    <path d="m6 9 6 6 6-6"/>
                                  </svg>
                                )}
                                <span style={{ 
                                  color: (activity.likes || 0) >= 0 ? '#00d4aa' : '#ef4444', 
                                  fontWeight: '600' 
                                }}>
                                  {activity.likes || 0}
                                </span>
                              </div>


                            {/* Downvotes (if you want to show them) */}
                            {activity.downvotes > 0 && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 8px',
                                background: 'rgba(239, 68, 68, 0.1)',
                                borderRadius: '8px',
                                border: '1px solid rgba(239, 68, 68, 0.2)'
                              }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#ef4444' }}>
                                  <path d="m6 9 6 6 6-6"/>
                                </svg>
                                <span style={{ color: '#ef4444', fontWeight: '600' }}>
                                  {activity.downvotes}
                                </span>
                              </div>
                            )}

                            {/* Comments/Replies */}
                            {(activity.replies > 0 || activity.comments > 0) && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 8px',
                                background: 'rgba(0, 212, 170, 0.1)',
                                borderRadius: '8px',
                                border: '1px solid rgba(0, 212, 170, 0.2)'
                              }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#00d4aa' }}>
                                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                                </svg>
                                <span style={{ color: '#00d4aa', fontWeight: '600' }}>
                                  {activity.replies || activity.comments || 0}
                                </span>
                              </div>
                            )}

                            {/* Views */}
                            {activity.views > 0 && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 8px',
                                background: 'rgba(139, 92, 246, 0.1)',
                                borderRadius: '8px',
                                border: '1px solid rgba(139, 92, 246, 0.2)'
                              }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#8b5cf6' }}>
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                  <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                                <span style={{ color: '#8b5cf6', fontWeight: '600' }}>
                                  {activity.views}
                                </span>
                              </div>
                            )}

                            {/* Add any additional stats here */}
                            {activity.score && activity.score > 0 && (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 8px',
                                background: 'rgba(245, 158, 11, 0.1)',
                                borderRadius: '8px',
                                border: '1px solid rgba(245, 158, 11, 0.2)'
                              }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#f59e0b' }}>
                                  <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"></polygon>
                                </svg>
                                <span style={{ color: '#f59e0b', fontWeight: '600' }}>
                                  {activity.score}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '48px 24px',
                  color: '#888',
                  background: 'linear-gradient(135deg, #1f1f1f 0%, #2a2a2a 100%)',
                  borderRadius: '16px',
                  border: '1px solid #333'
                }}>
                  <div style={{
                    width: '64px',
                    height: '64px',
                    background: 'rgba(0, 212, 170, 0.1)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    border: '2px solid rgba(0, 212, 170, 0.2)'
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#00d4aa' }}>
                      <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"></polyline>
                    </svg>
                  </div>
                  <p style={{ 
                    margin: '0 0 8px 0', 
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#e5e5e5'
                  }}>
                    No recent activity yet
                  </p>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '14px',
                    color: '#888'
                  }}>
                    This user hasn't posted anything recently
                  </p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default PublicProfileView;